import { useEffect } from 'react';

/**
 * Make a panel rendered inside a MUI Popover grow *upward* from a pinned
 * bottom edge instead of MUI's default top-anchored downward growth.
 *
 * Why this exists: MUI Popover re-computes `paper.style.top` in several
 * places (mount useEffect, the Grow transition's onEntering callback,
 * and a debounced window.resize). Each write would otherwise yank the
 * panel back to its top-anchored position. We swap `top` -> `bottom` on
 * every style mutation so the bottom edge stays fixed; only the top edge
 * moves when content grows or shrinks.
 *
 * The hook also drives an invisible "guard" element sitting above the
 * panel. When the panel shrinks (e.g. the user collapsed a section), the
 * guard expands to cover the area the panel used to occupy. As a React
 * descendant of the parent menu item, the guard keeps the mouse inside
 * the submenu's React tree so the parent NestedMenuItem doesn't fire
 * `mouseLeave` and close everything.
 *
 * Caveats — important if you edit this:
 *   - DOM coupling. `closest('.MuiPopover-paper')` is the MUI v5 paper
 *     class. If MUI's class structure changes, or if this hook is ever
 *     used outside a Popover, the effect silently no-ops.
 *   - Monotonic guard height. The guard only grows; it never shrinks.
 *     That's intentional: a shrinking guard would let mouseLeave fire
 *     when the panel collapses, which is the very thing the guard is
 *     here to prevent. If you "fix" this, the footer will appear to
 *     slide off-screen on the first expand.
 *   - SSR / jsdom. ResizeObserver and MutationObserver are not part of
 *     jsdom, so the hook feature-detects them. In a non-browser
 *     environment the panel just doesn't get the upward-growth /
 *     shrink-guard behaviour (it'll still render).
 *
 * @param {React.MutableRefObject<HTMLElement|null>} wrapperRef
 *   Ref pointing at the visible panel element (the one with bgcolor,
 *   boxShadow, etc.). Must be a descendant of the Popover's Paper.
 * @param {React.MutableRefObject<HTMLElement|null>} guardRef
 *   Ref pointing at the invisible guard div positioned directly above
 *   the wrapper. Its `style.height` is driven by this hook.
 */
export default function usePinPopoverBottom(wrapperRef, guardRef) {
    useEffect(() => {
        if (!wrapperRef.current) return undefined;
        const wrapper = wrapperRef.current;
        const paper = wrapper.closest('.MuiPopover-paper');
        if (!paper) return undefined;

        paper.style.background = 'transparent';
        paper.style.boxShadow = 'none';
        paper.style.overflow = 'visible';

        let pinning = false;
        const pinBottom = () => {
            if (pinning) return;
            const topStr = paper.style.top;
            if (!topStr || topStr === 'auto') return;
            const top = parseFloat(topStr);
            if (Number.isNaN(top)) return;
            const layoutBottom = top + paper.offsetHeight;
            pinning = true;
            paper.style.top = 'auto';
            paper.style.bottom = `${window.innerHeight - layoutBottom}px`;
            pinning = false;
        };
        pinBottom();

        const disposers = [];
        if (typeof MutationObserver !== 'undefined') {
            const styleObserver = new MutationObserver(() => pinBottom());
            styleObserver.observe(paper, { attributes: true, attributeFilter: ['style'] });
            disposers.push(() => styleObserver.disconnect());
        }
        if (typeof ResizeObserver !== 'undefined') {
            let maxHeight = 0;
            const sizeObserver = new ResizeObserver((entries) => {
                const { height } = entries[0].contentRect;
                if (height > maxHeight) maxHeight = height;
                if (guardRef.current) {
                    guardRef.current.style.height = `${Math.max(0, maxHeight - height)}px`;
                }
            });
            sizeObserver.observe(wrapper);
            disposers.push(() => sizeObserver.disconnect());
        }

        return () => disposers.forEach((dispose) => dispose());
    }, [wrapperRef, guardRef]);
}
